import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Shuffle,
  Repeat,
  Volume2,
  VolumeX,
  Music4,
  ArrowLeft,
  ListMusic,
} from "lucide-react";

const fmt = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

// Deterministic Fisher-Yates so shuffles feel fresh without depending on Math.random inconsistencies.
const shuffleArr = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Detect YouTube URLs so we can route them to an iframe player instead of <audio>.
const YT_RE = /(?:youtube\.com\/(?:watch\?v=|live\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const youtubeIdOf = (url) => {
  if (!url) return null;
  const m = url.match(YT_RE);
  return m ? m[1] : null;
};

// Lazy-loads the YouTube IFrame API exactly once, returning window.YT.
let ytApiPromise = null;
const loadYT = () => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    // The API calls this global when it's ready; if something else already
    // registered a handler, chain ours after it.
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try { prev && prev(); } catch (_e) { /* noop */ }
      resolve(window.YT);
    };
  });
  return ytApiPromise;
};

const OnDemand = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [order, setOrder] = useState([]);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);

  const audioRef = useRef(null);
  const ytHostRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const ytProgressTimerRef = useRef(null);
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const [searchParams, setSearchParams] = useSearchParams();
  // Tracks whether we've already consumed the initial ?track=… deep-link so we
  // don't fight the user if they manually skip afterwards.
  const consumedDeepLinkRef = useRef(false);
  // Tracks IDs whose play count has already been reported this session — prevents
  // one long listen (with pauses/resumes) from inflating the total.
  const reportedRef = useRef(new Set());

  // Load full library once on mount.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/on-demand/tracks")
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setTracks(list);
        setOrder(list.map((_, i) => i));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Re-shuffle order whenever the user toggles the shuffle button on.
  useEffect(() => {
    if (tracks.length === 0) return;
    setOrder(shuffle ? shuffleArr(tracks.map((_, i) => i)) : tracks.map((_, i) => i));
    setIdx(0);
  }, [shuffle, tracks]);

  // Consume ?track=…&autoplay=1 deep-link once the library is loaded.
  // Also strip the query params after jumping so refresh doesn't re-trigger.
  useEffect(() => {
    if (consumedDeepLinkRef.current || tracks.length === 0) return;
    const target = searchParams.get("track");
    if (!target) return;
    const realIdx = tracks.findIndex((t) => t.id === target);
    if (realIdx >= 0) {
      // We may not yet have the shuffled order — jump using the currently rendered order.
      const pos = order.indexOf(realIdx);
      if (pos >= 0) setIdx(pos); else setIdx(realIdx);
      if (searchParams.get("autoplay") === "1") setPlaying(true);
      consumedDeepLinkRef.current = true;
      // Preserve any non-track params by rebuilding without ours.
      const next = new URLSearchParams(searchParams);
      next.delete("track");
      next.delete("autoplay");
      setSearchParams(next, { replace: true });
    }
  }, [tracks, order, searchParams, setSearchParams]);

  const current = useMemo(() => {
    if (tracks.length === 0 || order.length === 0) return null;
    const real = order[idx % order.length];
    return tracks[real] || null;
  }, [tracks, order, idx]);

  const currentYtId = current ? youtubeIdOf(current.url) : null;
  const isYT = Boolean(currentYtId);

  // Route current track to the correct player: <audio> for uploads / MP3 URLs,
  // hidden YouTube iframe (via YT.Player) for healing frequency links.
  useEffect(() => {
    const a = audioRef.current;
    if (!current) return;

    // Kill any leftover progress polling from a previous YouTube track.
    if (ytProgressTimerRef.current) {
      clearInterval(ytProgressTimerRef.current);
      ytProgressTimerRef.current = null;
    }

    if (isYT) {
      // Pause the HTML5 audio element so we're not double-playing.
      try { if (a) { a.pause(); a.removeAttribute("src"); a.load(); } } catch (_e) { /* noop */ }
      setProgress(0);
      setDuration(0);

      loadYT().then((YT) => {
        if (!YT || !ytHostRef.current) return;
        // Destroy any previous instance so we always mount a fresh one for the new video.
        try { ytPlayerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
        ytPlayerRef.current = new YT.Player(ytHostRef.current, {
          host: "https://www.youtube-nocookie.com",
          videoId: currentYtId,
          playerVars: { autoplay: playing ? 1 : 0, controls: 0, playsinline: 1, modestbranding: 1, rel: 0 },
          events: {
            onReady: (e) => {
              try {
                e.target.setVolume(Math.round(volume * 100));
                if (muted) e.target.mute(); else e.target.unMute();
                if (playing) e.target.playVideo();
                setDuration(e.target.getDuration() || 0);
              } catch (_e) { /* noop */ }
              // Poll YT for progress since it doesn't emit timeupdate.
              ytProgressTimerRef.current = setInterval(() => {
                try {
                  const p = ytPlayerRef.current;
                  if (!p || !p.getCurrentTime) return;
                  setProgress(p.getCurrentTime() || 0);
                  const d = p.getDuration();
                  if (d && d !== duration) setDuration(d);
                } catch (_e) { /* noop */ }
              }, 500);
            },
            onStateChange: (e) => {
              if (e.data === 1) setPlaying(true);
              else if (e.data === 2) setPlaying(false);
              else if (e.data === 0) { setPlaying(false); next(); }
            },
          },
        });
      });
    } else {
      // Native HTML5 audio path.
      try { ytPlayerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
      ytPlayerRef.current = null;
      if (!a) return;
      a.src = `${backendBase}${current.url}`;
      a.load();
      if (playing) {
        a.play().catch(() => setPlaying(false));
      }
    }
    return () => {
      if (ytProgressTimerRef.current) {
        clearInterval(ytProgressTimerRef.current);
        ytProgressTimerRef.current = null;
      }
    };
  }, [current, backendBase, isYT, currentYtId]);

  // Volume/mute wiring — pushed to whichever player is currently active.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
    const p = ytPlayerRef.current;
    try {
      if (p && p.setVolume) {
        p.setVolume(Math.round(volume * 100));
        if (muted) p.mute(); else p.unMute();
      }
    } catch (_e) { /* noop */ }
  }, [volume, muted]);

  const toggle = () => {
    if (isYT) {
      const p = ytPlayerRef.current;
      if (!p) return;
      try {
        if (playing) { p.pauseVideo(); setPlaying(false); }
        else { p.playVideo(); setPlaying(true); reportPlay(); }
      } catch (_e) { /* noop */ }
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  // Report a play to the backend once per track per session and reflect the new count locally.
  const reportPlay = () => {
    const id = current?.id;
    if (!id || reportedRef.current.has(id)) return;
    // Only report plays for real upload IDs — synthetic ids (ext:/healing:) aren't
    // backed by a file row so the backend would 404 and needlessly log an error.
    if (id.startsWith("ext:") || id.startsWith("healing:")) return;
    reportedRef.current.add(id);
    api
      .post(`/on-demand/plays/${id}`)
      .then((r) => {
        const newCount = r?.data?.plays;
        if (typeof newCount === "number") {
          setTracks((prev) => prev.map((t) => (t.id === id ? { ...t, plays: newCount } : t)));
        }
      })
      .catch(() => {});
  };

  const next = () => setIdx((i) => (order.length === 0 ? 0 : (i + 1) % order.length));
  const prev = () => setIdx((i) => (order.length === 0 ? 0 : (i - 1 + order.length) % order.length));
  const jumpTo = (realIndex) => {
    const pos = order.indexOf(realIndex);
    if (pos >= 0) {
      setIdx(pos);
      setPlaying(true);
      // The current-track effect will (re)mount the correct player and autoplay it.
    }
  };

  const onSeek = (e) => {
    if (!Number.isFinite(duration) || duration <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const t = ratio * duration;
    if (isYT) {
      try { ytPlayerRef.current?.seekTo?.(t, true); } catch (_e) { /* noop */ }
      setProgress(t);
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = t;
    setProgress(t);
  };

  return (
    <div className="min-h-screen text-[#FAFAFA]" data-testid="on-demand-root">
      <Navbar />
      <main className="mx-auto max-w-[1400px] px-4 pb-40 pt-10 sm:px-6">
        <Link
          to="/"
          data-testid="on-demand-back-link"
          className="inline-flex items-center gap-2 border border-white/10 bg-black/50 px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.28em] text-[#FAFAFA] backdrop-blur-sm transition-colors hover:border-[#A855F7] hover:text-[#A855F7]"
        >
          <ArrowLeft size={12} /> Home
        </Link>

        <div className="mt-8 flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center border border-[#A855F7] bg-[#A855F7]">
            <span className="font-[Unbounded,sans-serif] text-2xl font-bold leading-none text-black">B</span>
          </div>
          <div>
            <div className="font-mono text-xs font-bold uppercase tracking-[0.28em] text-[#A855F7]">
              Black Box Music
            </div>
            <h1 className="mt-1 font-[Unbounded,sans-serif] text-3xl font-bold uppercase leading-tight tracking-tight sm:text-4xl">
              On Demand
            </h1>
          </div>
        </div>

        <p className="mt-4 max-w-2xl font-mono text-sm font-bold leading-relaxed text-[#FAFAFA]">
          The complete Black Box Music library — every uploaded track, playing in constant rotation.
          Skip freely, shuffle, or let it run.
        </p>

        {/* Now-playing card */}
        <section
          data-testid="on-demand-player-card"
          className="mt-10 grid gap-6 border border-white/10 bg-black/70 p-6 backdrop-blur-md md:grid-cols-[220px,1fr] md:p-8"
        >
          <div className="relative flex aspect-square w-full max-w-[220px] items-center justify-center overflow-hidden border border-white/10 bg-black">
            <img
              src="/img/logo.jpg"
              alt="Black Box Music"
              className="h-full w-full object-contain"
              style={{ mixBlendMode: "screen" }}
              draggable={false}
            />
            {playing ? (
              <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className="block w-[3px] rounded-sm bg-[#A855F7]"
                    style={{
                      height: 14,
                      transformOrigin: "bottom",
                      animation: `bbm-od-eq 900ms ${i * 120}ms ease-in-out infinite alternate`,
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div className="flex min-w-0 flex-col justify-between gap-6">
            <div className="min-w-0">
              <div className="font-mono text-xs font-bold uppercase tracking-[0.28em] text-[#A855F7]">
                {loading ? "Loading library…" : `${tracks.length} tracks · Now playing`}
              </div>
              <div
                data-testid="on-demand-track-title"
                className="mt-2 truncate font-[Unbounded,sans-serif] text-2xl font-bold leading-tight text-[#FAFAFA] sm:text-3xl"
                title={current?.title || ""}
              >
                {current?.title || (loading ? "…" : "No tracks yet")}
              </div>
              <div className="mt-1 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
                Black Box Music · Uploaded
                {typeof current?.plays === "number" ? (
                  <span data-testid="on-demand-current-plays" className="ml-2 text-[#A855F7]">
                    · {current.plays.toLocaleString()} play{current.plays === 1 ? "" : "s"}
                  </span>
                ) : null}
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={onSeek}
                data-testid="on-demand-progress-bar"
                aria-label="Seek"
                className="group relative h-2 w-full cursor-pointer border border-white/10 bg-black/60"
              >
                <div
                  className="absolute inset-y-0 left-0 bg-[#A855F7] transition-[width]"
                  style={{ width: duration > 0 ? `${(progress / duration) * 100}%` : "0%" }}
                />
              </button>
              <div className="flex items-center justify-between font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
                <span data-testid="on-demand-progress-time">{fmt(progress)}</span>
                <span data-testid="on-demand-duration-time">{fmt(duration)}</span>
              </div>
            </div>

            {/* Transport controls */}
            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setShuffle((s) => !s)}
                data-testid="on-demand-shuffle"
                aria-label="Shuffle"
                className={`flex h-10 w-10 items-center justify-center border transition-colors ${
                  shuffle
                    ? "border-[#A855F7] bg-[#A855F7]/10 text-[#A855F7]"
                    : "border-white/10 text-[#A1A1AA] hover:border-white/30 hover:text-[#FAFAFA]"
                }`}
              >
                <Shuffle size={16} />
              </button>

              <div className="flex flex-1 items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={prev}
                  data-testid="on-demand-prev"
                  aria-label="Previous"
                  className="flex h-11 w-11 items-center justify-center border border-white/15 text-[#FAFAFA] transition-colors hover:border-[#A855F7] hover:text-[#A855F7]"
                >
                  <SkipBack size={18} strokeWidth={2.5} />
                </button>
                <button
                  type="button"
                  onClick={toggle}
                  data-testid="on-demand-toggle"
                  aria-label={playing ? "Pause" : "Play"}
                  disabled={!current}
                  className="flex h-14 w-14 items-center justify-center border border-[#A855F7] bg-[#A855F7] text-black transition-colors hover:bg-transparent hover:text-[#A855F7] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {playing ? <Pause size={22} strokeWidth={2.5} /> : <Play size={22} strokeWidth={2.5} />}
                </button>
                <button
                  type="button"
                  onClick={next}
                  data-testid="on-demand-next"
                  aria-label="Next"
                  className="flex h-11 w-11 items-center justify-center border border-white/15 text-[#FAFAFA] transition-colors hover:border-[#A855F7] hover:text-[#A855F7]"
                >
                  <SkipForward size={18} strokeWidth={2.5} />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMuted((m) => !m)}
                  data-testid="on-demand-mute"
                  aria-label={muted ? "Unmute" : "Mute"}
                  className="flex h-10 w-10 items-center justify-center text-[#A1A1AA] transition-colors hover:text-[#FAFAFA]"
                >
                  {muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(volume * 100)}
                  onChange={(e) => { setVolume(Number(e.target.value) / 100); if (Number(e.target.value) > 0) setMuted(false); }}
                  data-testid="on-demand-volume"
                  aria-label="Volume"
                  className="h-1 w-24 cursor-pointer accent-[#A855F7]"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
              <Repeat size={12} /> Auto-plays next track. Repeats library forever.
            </div>
          </div>
        </section>

        {/* Playlist */}
        <section className="mt-10">
          <div className="flex items-center gap-2 border-b border-white/10 pb-3">
            <ListMusic size={14} className="text-[#A855F7]" />
            <h2 className="font-mono text-sm font-bold uppercase tracking-[0.28em] text-[#FAFAFA]">
              Library ({tracks.length})
            </h2>
          </div>
          <ul className="mt-4 divide-y divide-white/5" data-testid="on-demand-playlist">
            {loading && tracks.length === 0 ? (
              <li className="py-10 text-center font-mono text-sm font-bold text-[#A1A1AA]">Loading…</li>
            ) : tracks.length === 0 ? (
              <li className="py-10 text-center font-mono text-sm font-bold text-[#A1A1AA]">
                No tracks uploaded yet. Upload some via the admin dashboard.
              </li>
            ) : (
              tracks.map((t, i) => {
                const active = current?.id === t.id;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => jumpTo(i)}
                      data-testid={`on-demand-track-${i}`}
                      className={`flex w-full items-center gap-4 px-2 py-3 text-left transition-colors ${
                        active ? "bg-[#A855F7]/10" : "hover:bg-white/5"
                      }`}
                    >
                      <span className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A855F7]" style={{ minWidth: 24 }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center border ${active ? "border-[#A855F7] text-[#A855F7]" : "border-white/10 text-[#A1A1AA]"}`}>
                        {active && playing ? <Pause size={12} /> : <Music4 size={12} />}
                      </span>
                      <span className={`min-w-0 flex-1 truncate font-mono text-sm font-bold ${active ? "text-[#A855F7]" : "text-[#FAFAFA]"}`}>
                        {t.title}
                      </span>
                      <span
                        data-testid={`on-demand-track-plays-${i}`}
                        className="hidden flex-shrink-0 font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A1A1AA] sm:inline"
                      >
                        {(t.plays || 0).toLocaleString()} play{(t.plays || 0) === 1 ? "" : "s"}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </section>
      </main>

      <Footer />

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload="none"
        onEnded={next}
        onPlay={() => { setPlaying(true); reportPlay(); }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime || 0)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        data-testid="on-demand-audio"
      />
      {/* Hidden YouTube host — YT.Player replaces this div with an <iframe>. */}
      <div
        style={{ position: "fixed", left: "-9999px", top: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        aria-hidden="true"
        data-testid="on-demand-yt-host-wrap"
      >
        <div ref={ytHostRef} data-testid="on-demand-yt-host" />
      </div>

      <style>{`
        @keyframes bbm-od-eq {
          from { transform: scaleY(0.35); }
          to   { transform: scaleY(1.35); }
        }
      `}</style>
    </div>
  );
};

export default OnDemand;
